// frontend/src/components/EventSwitcher.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { adminService } from '../services/supabaseClient';

const EventSwitcher = ({ currentEventCode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await adminService.getAllEvents();
      if (!error && data) {
        // Filter out archived events
        setEvents(data.filter(e => !e.is_archived));
      }
      setLoading(false);
    };
    fetchEvents();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.event-switcher')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleEventChange = (eventCode) => {
    // Preserve the current path structure, just change the event code
    const pathParts = location.pathname.split('/');

    if (pathParts[1] === 'admin') {
      // If we're in admin section
      if (pathParts.length > 3) {
        // If we're in team detail, go back to dashboard for new event
        navigate(`/admin/${eventCode}`);
      } else {
        // If we're in dashboard, just switch event
        navigate(`/admin/${eventCode}`);
      }
    }

    setIsOpen(false);
  };

  const currentEvent = events.find(e => e.code === currentEventCode);
  const currentEventName = currentEvent ? currentEvent.short_name : currentEventCode?.toUpperCase();

  return (
    <div className="event-switcher relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 rounded-full transition-all duration-300 text-white shadow-lg"
      >
        <span className="text-xs font-black uppercase tracking-widest opacity-70">Event:</span>
        <span className="text-sm font-black uppercase tracking-tight">{loading ? '...' : currentEventName}</span>
        <svg
          className={`w-3 h-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-3 w-72 bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-gray-100 overflow-hidden z-[100] animate-in fade-in zoom-in duration-200">
          <div className="p-2 max-h-80 overflow-y-auto custom-scrollbar">
            {events.map((event) => (
              <button
                key={event.code}
                onClick={() => handleEventChange(event.code)}
                className={`w-full text-left p-4 rounded-2xl transition-all duration-200 group ${currentEventCode === event.code
                  ? 'bg-mjwc-primary text-white shadow-lg'
                  : 'hover:bg-gray-50 text-gray-700'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span className={`block text-xs font-black uppercase tracking-wider mb-1 ${currentEventCode === event.code ? 'text-white' : 'text-gray-900 group-hover:text-mjwc-primary'}`}>
                      {event.short_name}
                    </span>
                    <span className={`text-[10px] font-medium block truncate ${currentEventCode === event.code ? 'text-white/60' : 'text-gray-400'}`}>
                      {event.location}
                    </span>
                  </div>
                  {currentEventCode === event.code ? (
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-3.5 h-3.5 text-mjwc-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="p-2 border-t border-gray-50 bg-gray-50/50">
            <button
              onClick={() => navigate('/admin/events')}
              className="w-full p-4 text-[10px] font-black uppercase tracking-widest text-mjwc-primary hover:bg-white rounded-2xl transition-all flex items-center justify-center gap-2 border border-transparent hover:border-mjwc-primary/10"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Manage All Events
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventSwitcher;